import React, { useState, useEffect } from 'react';
import { getRenewalCalendar, createRenewal } from '../services/assujettiService';

interface Renewal {
  id: string;
  license_id: string;
  establishment_name: string;
  category: string;
  duration_months: number;
  expiry_date: string;
  amount: number;
  currency: string;
  days_remaining: number;
}

function alertBadge(days: number) {
  if (days <= 0) return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Expiree</span>;
  if (days <= 30) return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">30 jours</span>;
  if (days <= 90) return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">90 jours</span>;
  return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">A venir</span>;
}

export default function V4RenewalsPage() {
  const [rows, setRows] = useState<Renewal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<Renewal | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    getRenewalCalendar()
      .then((r: any) => {
        const list = Array.isArray(r) ? r : r.data ?? r.renewals ?? [];
        setRows(list.map((e: any) => ({
          ...e,
          days_remaining: e.days_remaining ?? Math.ceil((new Date(e.expiry_date).getTime() - Date.now()) / 86400000),
        })));
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const within90 = rows.filter(r => r.days_remaining > 0 && r.days_remaining <= 90).length;
  const within30 = rows.filter(r => r.days_remaining > 0 && r.days_remaining <= 30).length;
  const expired = rows.filter(r => r.days_remaining <= 0).length;
  const totalAmount = rows.filter(r => r.days_remaining <= 90).reduce((s, r) => s + (r.amount || 0), 0);

  const handleSubmit = async () => {
    if (!dialog) return;
    setSubmitting(true);
    try {
      await createRenewal(dialog.license_id, notes || undefined);
      setDialog(null);
      setNotes('');
      load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto py-10 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" /><p className="mt-4 text-gray-500">Chargement...</p></div>;
  if (error) return <div className="max-w-7xl mx-auto py-10"><div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div></div>;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Calendrier des renouvellements</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Dans 90 jours', value: within90, color: 'text-orange-600' },
          { label: 'Dans 30 jours', value: within30, color: 'text-red-600' },
          { label: 'Expirees', value: expired, color: 'text-red-700' },
          { label: 'Montant a prevoir', value: `${totalAmount.toLocaleString()} USD`, color: 'text-gray-900' },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">{k.label}</p>
            <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Aucun renouvellement prevu.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Etablissement', 'Categorie', 'Duree', 'Montant', 'Alerte', 'Action'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map(r => (
                <tr key={r.id || r.license_id} className="hover:bg-gray-50">
                  <td className="px-5 py-4 text-sm">{r.expiry_date ? new Date(r.expiry_date).toLocaleDateString('fr-FR') : '-'}</td>
                  <td className="px-5 py-4 text-sm font-medium">{r.establishment_name}</td>
                  <td className="px-5 py-4 text-sm">{r.category}</td>
                  <td className="px-5 py-4 text-sm font-semibold">{r.duration_months} mois</td>
                  <td className="px-5 py-4 text-sm">{r.amount?.toLocaleString()} {r.currency || 'USD'}</td>
                  <td className="px-5 py-4">{alertBadge(r.days_remaining)}</td>
                  <td className="px-5 py-4">
                    <button onClick={() => setDialog(r)} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700">Preparer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Renewal dialog */}
      {dialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">Preparer le renouvellement</h2>
            <div className="space-y-3 text-sm mb-4">
              <div><span className="text-gray-500">Etablissement :</span> {dialog.establishment_name}</div>
              <div><span className="text-gray-500">Categorie :</span> {dialog.category}</div>
              <div><span className="text-gray-500">Montant :</span> {dialog.amount?.toLocaleString()} {dialog.currency || 'USD'}</div>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg p-2 text-sm mb-4" />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setDialog(null); setNotes(''); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Annuler</button>
              <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Envoi...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
